/**
 * Shared IndexedDB version constants.
 *
 * DB_VERSION is the single source of truth for the 'mycrogym_studio' database version.
 *
 * Rules:
 *  - NEVER use tables.length or any dynamic value as a DB version.
 *  - NEVER lower this value once deployed — IndexedDB versions are monotonically increasing.
 *  - When adding a table to tables.json, increment DB_VERSION by 1 here (only here).
 *
 * All workers (query.worker.js, server.worker.js, DatabaseManager.ts) import this value.
 */
// 34 (2026-08-14): clients_attendance (Einlass — Checkin/Checkout) kommt zur
// tables.json dazu. Ohne diesen Bump existiert der Object Store in bereits
// installierten Browsern nicht, und der erste 'push' einer Anwesenheitszeile
// läuft in einen Dexie-Fehler.
export const DB_VERSION = 34

/**
 * Client-side data-schema version for the 'mycrogym_studio' data DB.
 *
 * This is DELIBERATELY separate from DB_VERSION and from the server-driven
 * params['schemaVersion']:
 *  - DB_VERSION           → Dexie/IndexedDB object-store & index structure.
 *  - params['schemaVersion'] → server manifest version; drives schemaGuard()'s
 *                              "server added a table" heal.
 *  - DATA_SCHEMA_VERSION  → this build's understanding of the *row shape* of the
 *                              cached data. Bump it by +1 whenever a new client
 *                              build changes how rows are stored in a way that is
 *                              INCOMPATIBLE with data written by an older build
 *                              (renamed/re-typed columns, changed index layout,
 *                              changed encoding). schemaGuard() then forces a
 *                              one-time healIndexedDB() (studio data only — the
 *                              config DB, logins and integrations are untouched)
 *                              and re-stamps params['data_schema_version'].
 *
 * The old production build predates this marker, so params['data_schema_version']
 * is ABSENT there — that absence (together with already-synced data) is exactly
 * how we detect a stale-schema install and reload its data on relaunch.
 *
 * NEVER lower this value once deployed.
 */
// v2 (2026-08-11): mgmt_languages, mgmt_template_catalog, mgmt_template_releases
// und mgmt_studio_purchases sind zur tables.json dazugekommen. Ein reiner
// DB_VERSION-Bump legt die Object-Stores zwar an, füllt sie aber nicht: der
// Sync-Modus (init/update) hängt am `ii`-Flag der Company, nicht an der Tabelle
// — bestehende Installationen hätten die neuen Stores dauerhaft leer gesehen und
// nur zufällig gerade geänderte Zeilen per Incremental bekommen. Der Bump hier
// erzwingt einmalig healIndexedDB() + ii=false und damit einen echten Init-Sync.
export const DATA_SCHEMA_VERSION = 2

// ── Config DB ('mycrogym_studio_config') ─────────────────────────────────────

export const CONFIG_DB_NAME = 'mycrogym_studio_config'

/**
 * Highest declared Dexie version of the config DB.
 * Bump it here — and ONLY here — when adding a store/index below.
 */
export const CONFIG_DB_VERSION = 26

/**
 * Stores whose primary key was changed from auto-increment ('++id' / '++param')
 * to a plain keyPath on 2026-06-11 (user ids are UUID strings, see LEARNINGS
 * "Safari/iOS IDB WebWorker Hang"). Map: store name → expected keyPath.
 *
 * IndexedDB cannot change a keyPath or the autoIncrement flag in place, so every
 * browser that still holds the old stores fails to open with
 *   UpgradeError: Not yet support for changing primary key
 * and — because Dexie closes the DB on a failed upgrade — every subsequent read
 * throws DatabaseClosedError. migrateLegacyConfigDb() below repairs those installs.
 */
const CONFIG_LEGACY_PK = { user: 'id', companies: 'id', params: 'param' }

/**
 * THE canonical config-DB version ladder.
 *
 * Historically this ladder was copy-pasted at five places (DBConfigService,
 * server.worker init(), DatabaseManager.ConfigDatabase, stores/actionList,
 * stores/activity) and drifted — a single diverging declaration makes Dexie 4
 * auto-bump the native version and force-close every other connection mid-sync.
 * All five now call this function; never declare stores for this DB elsewhere.
 *
 * @param {import('dexie').Dexie} db
 * @returns {import('dexie').Dexie} the same instance
 */
export function declareConfigSchema(db) {
    if (db.__configSchemaDeclared) return db
    db.version(1).stores({ user: 'id, em', companies: 'id', params: 'param' })
    db.version(2).stores({ user: 'id, em', companies: 'id', params: 'param' })
    db.version(21).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type'
    })
    db.version(22).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type',
        integrations: 'key, provider, companyid'
    })
    db.version(23).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type',
        integrations: 'key, provider, companyid',
        morning_report: 'key'   // key = 'report_<companyid>_<userid>'
    })
    // v24: ActionRulesEngine fallback stores
    db.version(24).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type',
        integrations: 'key, provider, companyid',
        morning_report: 'key',
        calendar_bookings: 'id,calid,status,archive',
        calendar_events: 'id,trainerid,datestart,archive'
    })
    // v25: calendareventid + clientid indexes on calendar_bookings
    db.version(25).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type',
        integrations: 'key, provider, companyid',
        morning_report: 'key',
        calendar_bookings: 'id,calid,status,archive,calendareventid,clientid',
        calendar_events: 'id,trainerid,datestart,archive'
    })
    // v26 (2026-09-16): pending_formprocess — Formularprozesse, die der Server
    // NICHT bestaetigt hat. Bewusst in der Config-DB und nicht in
    // 'mycrogym_studio': dort stehen ausschliesslich Zeilen, die der Server
    // kennt. Ein nicht uebertragener Vorgang, der in den Sync-Tabellen liegt,
    // ist von einem gespeicherten nicht zu unterscheiden — genau daran ist am
    // 16.09.2026 eine komplette Registrierung verschwunden. Hier kann ihn
    // niemand versehentlich fuer eine Kundenakte halten, und healIndexedDB()
    // (das nur die Datenbank 'mycrogym_studio' loescht) raeumt ihn nicht weg.
    db.version(CONFIG_DB_VERSION).stores({
        user: 'id, em', companies: 'id', params: 'param',
        system_activities: 'id, timestamp, status, type',
        integrations: 'key, provider, companyid',
        morning_report: 'key',
        calendar_bookings: 'id,calid,status,archive,calendareventid,clientid',
        calendar_events: 'id,trainerid,datestart,archive',
        pending_formprocess: 'id, companyid, clientid, processtemplateid, updated'
    })
    db.__configSchemaDeclared = true
    return db
}

/** True for the (possibly wrapped) Dexie error that a PK change produces. */
function isPrimaryKeyUpgradeError(err) {
    if (!err) return false
    const text = `${err.name || ''} ${err.message || ''} ${err.inner?.name || ''} ${err.inner?.message || ''}`
    return text.includes('changing primary key') || text.includes('UpgradeError')
}

function idbRequest(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
    })
}

/**
 * Compare every store's ACTUAL primary key against the expected one, read the
 * rows out of those that differ and delete them, so the next Dexie open
 * recreates them with the correct primary key.
 *
 * Works off the real IndexedDB schema instead of a version rung — which store a
 * given browser is wrong about depends on which build last touched it, and a
 * rung only runs for `version >= oldVersion`.
 *
 * Returns the rescued rows ({} when there is nothing to migrate) — they are put
 * back by restoreRescuedRows() once Dexie has opened. Rescuing matters: config
 * `user` holds the login (kl/at/rt), `params` the device_secret those tokens are
 * encrypted with, and `companies` the ii flags — dropping them logs every user
 * out and forces a full init-sync for everyone.
 *
 * @param {string} dbName
 * @param {Object} expected - { storeName: { keyPath, auto } | null }; null = leave alone
 * @param {string} tag - log prefix
 * @param {number} targetVersion - the Dexie version the caller will open at
 */
async function rescueAndDropMismatchedStores(dbName, expected, tag, targetVersion) {
    const idb = globalThis.indexedDB
    if (!idb) return {}

    // No version argument = open at whatever version the browser has.
    // (Creates an empty DB when none exists — harmless, Dexie upgrades it.)
    const probe = await idbRequest(idb.open(dbName))
    const mismatched = []
    for (const [name, want] of Object.entries(expected)) {
        if (!want || !probe.objectStoreNames.contains(name)) continue
        const store = probe.transaction(name, 'readonly').objectStore(name)
        if (store.autoIncrement !== want.auto || String(store.keyPath || '') !== want.keyPath) {
            mismatched.push(name)
        }
    }
    if (mismatched.length === 0) {
        probe.close()
        return {}
    }

    console.warn(`${tag} stores with an outdated primary key: ${mismatched.join(', ')} — migrating`)
    const rescued = {}
    for (const name of mismatched) {
        try {
            rescued[name] = await idbRequest(probe.transaction(name, 'readonly').objectStore(name).getAll())
        } catch (err) {
            console.warn(`${tag} could not read "${name}" before migration — its rows are lost:`, err)
            rescued[name] = []
        }
    }
    // Stay below the version Dexie is heading for, so Dexie recreates the stores
    // itself in its normal upgrade (with exactly the indexes it expects).
    const dropVersion = Math.min(probe.version + 1, targetVersion * 10 - 1)
    probe.close()

    await new Promise((resolve, reject) => {
        const request = idb.open(dbName, dropVersion)
        const timer = setTimeout(
            () => reject(new Error(`${tag} migration blocked by another connection for 10s`)),
            10000
        )
        request.onupgradeneeded = () => {
            for (const name of mismatched) request.result.deleteObjectStore(name)
        }
        request.onblocked = () => console.warn(`${tag} migration waiting for other connections to close…`)
        request.onsuccess = () => { clearTimeout(timer); request.result.close(); resolve() }
        request.onerror = () => { clearTimeout(timer); reject(request.error) }
    })

    const counts = Object.entries(rescued).map(([n, r]) => `${n}:${r.length}`).join(' ')
    console.log(`${tag} outdated stores dropped, rows held for restore (${counts})`)
    return rescued
}

/**
 * Primary key a Dexie index spec asks for. Returns null for anything this
 * migration must not touch (compound keys, outbound keys) — better to leave a
 * store alone than to guess at its shape.
 *
 * '++id,clientid' → { keyPath: 'id', auto: true } ; 'id' → { keyPath: 'id', auto: false }
 */
function parsePrimaryKey(indexSpec) {
    const first = String(indexSpec || '').split(',')[0].trim()
    if (!first) return null                                  // outbound key
    const auto = first.startsWith('++')
    const keyPath = auto ? first.slice(2).trim() : first
    if (!keyPath || /[[\]&*]/.test(keyPath)) return null      // compound / unique / multiEntry
    return { keyPath, auto }
}

function rescueAndDropLegacyConfigStores(dbName) {
    const expected = {}
    for (const [name, keyPath] of Object.entries(CONFIG_LEGACY_PK)) expected[name] = { keyPath, auto: false }
    return rescueAndDropMismatchedStores(dbName, expected, '[ConfigDB]', CONFIG_DB_VERSION)
}

// One migration attempt per JS context (main thread, server worker, query worker).
let configMigration = null

/**
 * Probe the config DB for legacy primary keys and repair it. Idempotent, cheap
 * for healthy installs (one open + three schema reads), and safe to call from
 * every context — whoever gets there first does the work, the others then see a
 * modern schema and no-op.
 *
 * @returns {Promise<Object>} rescued rows, keyed by store name
 */
export function migrateLegacyConfigDb(dbName = CONFIG_DB_NAME) {
    if (!configMigration) {
        configMigration = rescueAndDropLegacyConfigStores(dbName).catch((err) => {
            console.error('[ConfigDB] primary-key migration failed:', err)
            return {}
        })
    }
    return configMigration
}

/**
 * Put the rescued rows back after Dexie recreated the stores.
 * Rows written under '++id' still carry their key inline (IndexedDB writes the
 * generated key into the keyPath), so they survive the switch to a plain key.
 */
async function restoreRescuedRows(db, rescued, keyPathOf, tag) {
    for (const [name, rows] of Object.entries(rescued || {})) {
        if (!rows || rows.length === 0) continue
        const keyPath = keyPathOf(name)
        const valid = keyPath
            ? rows.filter((row) => row && row[keyPath] !== undefined && row[keyPath] !== null)
            : rows
        try {
            await db.table(name).bulkPut(valid)
            console.log(`${tag} restored ${valid.length}/${rows.length} rows into "${name}"`)
        } catch (err) {
            console.warn(`${tag} could not restore rows into "${name}":`, err)
        }
    }
}

/**
 * Open the config DB: declare the canonical ladder, migrate legacy primary keys
 * if needed, open, and put the rescued rows back.
 *
 * The UpgradeError catch is the safety net for the race in which another context
 * opened (and started upgrading) the DB while our probe still saw the old schema.
 *
 * @param {import('dexie').Dexie} dexieInstance
 * @returns {Promise<import('dexie').Dexie>} the same instance, opened
 */
export async function openConfigDb(dexieInstance) {
    declareConfigSchema(dexieInstance)
    let rescued = await migrateLegacyConfigDb(dexieInstance.name)
    try {
        await dexieInstance.open()
    } catch (err) {
        if (!isPrimaryKeyUpgradeError(err)) throw err
        console.warn('[ConfigDB] open still hit a primary-key upgrade — re-probing:', err.message)
        configMigration = null
        rescued = await migrateLegacyConfigDb(dexieInstance.name)
        dexieInstance.close()   // clears Dexie's cached dbOpenError so open() retries
        await dexieInstance.open()
    }
    await restoreRescuedRows(dexieInstance, rescued, (name) => CONFIG_LEGACY_PK[name], '[ConfigDB]')
    return dexieInstance
}

/**
 * Canonical record shape for the config DB 'companies' table
 * ('mycrogym_studio_config' → 'companies').
 *
 * Historically this table was written by several code paths with completely
 * different shapes (raw MySQL company row vs. sync-state stub), so rows were
 * inconsistent across browsers and the archive flag went stale. Every writer
 * MUST go through this merge so all rows carry the same canonical fields:
 *
 *   id, name, archive (0|1 number), datestart, dateend, lastupdate,
 *   ii (boolean), spk, cpuk, cprk
 *
 * Unknown extra fields on existing rows are preserved (merge, not replace).
 *
 * @param {Object|null|undefined} existing - current row from the config DB
 * @param {Object} update - fields to apply on top
 * @returns {Object} normalized merged row
 */
export function mergeConfigCompany(existing, update) {
    const merged = { ...(existing || {}), ...(update || {}) }
    merged.archive = Number(merged.archive) ? 1 : 0
    merged.ii = merged.ii === true
    if (merged.name === undefined) merged.name = ''
    if (merged.datestart === undefined) merged.datestart = ''
    if (merged.dateend === undefined) merged.dateend = ''
    if (merged.lastupdate === undefined) merged.lastupdate = ''
    if (merged.spk === undefined) merged.spk = ''
    if (merged.cpuk === undefined) merged.cpuk = ''
    if (merged.cprk === undefined) merged.cprk = ''
    return merged
}

/**
 * Open a Dexie database at the given version with VersionError auto-recovery.
 *
 * Why VersionError happens at all: Dexie 4 silently bumps the NATIVE IndexedDB
 * version by +1 when it has to install object stores that are missing at the
 * declared version (e.g. native 280 → 281). The server worker and the query
 * worker declare slightly different table sets at the same DB_VERSION, so one
 * of them bumps the native version and the other then sees native > declared.
 *
 * Recovery strategy: ATTACH DYNAMICALLY (open without a declared schema) at
 * whatever version the browser has. NEVER delete the database here — deleting
 * caused a wipe-loop (each worker recreated the DB at 280, the other bumped it
 * to 281 again, next page load wiped it again), leaving all tables empty while
 * the config DB still said ii=true → only incremental syncs → permanently
 * empty local cache (no ACL rights, no data).
 *
 * @param {import('dexie').Dexie} dexieInstance - A fresh, not-yet-open Dexie instance
 * @param {Object} schema - { tableName: indexSpec } map
 * @returns {Promise<import('dexie').Dexie>} The opened Dexie instance
 */
export async function openStudioDb(dexieInstance, schema) {
    const Dexie = dexieInstance.constructor
    const dbName = dexieInstance.name

    async function tryOpen(instance) {
        // Exactly ONE version rung. A `version(29).stores({ user: null })` rung
        // used to sit in front of it to drop the user table (its primary key
        // changed from '++id' to 'id'), and it was actively dangerous:
        //   - A rung's schema is the MERGE of all rungs up to it. With nothing
        //     declared below 29, that merge is EMPTY — and Dexie's
        //     deleteRemovedTables() drops every store missing from a rung's
        //     schema. Every install below v29 therefore lost ALL cached tables,
        //     while the config DB still said ii=true → incremental syncs only →
        //     permanently empty app. Verified against fake-indexeddb.
        //   - It did not even do its job in between: Dexie only runs rungs with
        //     version >= oldVersion, so installs at v30–33 skipped the drop and
        //     ran straight into "Not yet support for changing primary key".
        // Primary-key changes are handled version-independently below instead.
        instance.version(DB_VERSION).stores(schema)
        try {
            await instance.open()
            return instance
        } catch (err) {
            if (isPrimaryKeyUpgradeError(err)) {
                // A store's primary key changed (e.g. user: '++id' → 'id').
                // IndexedDB cannot change a keyPath in place: rescue the rows,
                // drop the store, let Dexie recreate it, put the rows back.
                console.warn(`[DB] "${dbName}" has stores with an outdated primary key — migrating:`, err.message)
                const expected = {}
                for (const [name, spec] of Object.entries(schema)) expected[name] = parsePrimaryKey(spec)
                const rescued = await rescueAndDropMismatchedStores(dbName, expected, '[StudioDB]', DB_VERSION)
                instance.close()
                await instance.open()
                await restoreRescuedRows(instance, rescued, (name) => expected[name]?.keyPath, '[StudioDB]')
                return instance
            }
            if (err.name === 'VersionError') {
                console.warn(
                    `[DB] VersionError on "${dbName}" — browser version is higher than declared ${DB_VERSION}.`,
                    'Attaching dynamically at the existing version (NO data is deleted).',
                    err.message
                )
                if (instance.isOpen()) instance.close()
                const fresh = new Dexie(dbName)
                fresh.on('blocked', () => false)
                // Dynamic mode: no version/stores declaration — Dexie adopts the
                // existing native version and exposes all existing object stores.
                await fresh.open()
                console.log(`[DB] "${dbName}" attached dynamically at native version ${fresh.verno * 10}`)
                return fresh
            }
            throw err
        }
    }

    return tryOpen(dexieInstance)
}
